//src/pages/FormSettingsPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, TextField, CircularProgress, Stack } from '@mui/material';
import { useLoading } from '../contexts/LoadingContext'; // Import useLoading from LoadingContext
import { useFormContext } from '../contexts/FormContext'; // Import useFormContext from FormContext
import VanityUrlField from '../components/FormSettings/VanityUrlField';

const FormSettingsPage = () => {
  const {
    formId,
    formTitle,
    formDescription,
    setFormTitle,
    setFormDescription,
    fetchFormDetails,
    formDetails,          // ← get current form details (may include vanitySlug)
    isPublished 
  } = useFormContext(); // Capture formId, title, description, and setters from FormContext

  const { setLoading } = useLoading(); // Get setLoading from LoadingContext

  // Local state to manage live changes
  const [localFormTitle, setLocalFormTitle] = useState(''); // Local state for form title
  const [localFormDescription, setLocalFormDescription] = useState(''); // Local state for form description
  const [saving, setSaving] = useState(false); // State to handle save action
  const [isModified, setIsModified] = useState(false); // Track if changes were made
  const [isFormLoaded, setIsFormLoaded] = useState(false); // Track form loading status

  // ─────────────────────────────────────────────────────────────
  // VANITY URL
  // The committed slug lives in forms/{formId}.vanitySlug.
  // Saves, edits, and removals are handled directly by VanityUrlField
  // through the backend setFormVanitySlug callable.
  const [initialVanitySlug, setInitialVanitySlug] = useState(''); // what’s currently committed (if known)

  // Seed initial vanity from formDetails when loaded.
  // Always clear local state if the backend value is removed.
  useEffect(() => {
    setInitialVanitySlug(formDetails?.vanitySlug || '');
  }, [formDetails?.vanitySlug]);

  // Ensure that form details are fetched from Firebase or session/local storage
  useEffect(() => {
    const loadFormDetails = async () => {
      // console.log(`[FormSettingsPage] Loading form details for formId: ${formId}`);
      setLoading(true);
      try {
        await fetchFormDetails(formId); // Ensure Firebase is prioritized
        // console.log(`[FormSettingsPage] Form details fetched from Firebase for formId: ${formId}`);
        setIsFormLoaded(true); // Set form as loaded after fetching
      } catch (error) {
        console.error(`[FormSettingsPage] Error fetching form details from Firebase: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      loadFormDetails(); // Fetch form details when formId is available
    } else {
      console.log('[FormSettingsPage] No formId available yet.');
    }
  }, [formId, fetchFormDetails, setLoading]);

  // Sync with FormContext values when they are updated
  useEffect(() => {
    if (isFormLoaded) {
      // console.log(`[FormSettingsPage] Syncing local state with FormContext for formId: ${formId}`);
      // console.log(`[FormSettingsPage] Current formTitle in FormContext: ${formTitle}`);
      // console.log(`[FormSettingsPage] Current formDescription in FormContext: ${formDescription}`);

      // Only sync if valid data is available in FormContext
      if (formTitle !== null && formTitle !== undefined) {
        // console.log(`[FormSettingsPage] Setting local form title: ${formTitle}`);
        setLocalFormTitle(formTitle); // Sync local title with FormContext
      } else {
        console.log('[FormSettingsPage] formTitle is null or undefined, not updating localFormTitle.');
      }

      if (formDescription !== null && formDescription !== undefined) {
        // console.log(`[FormSettingsPage] Setting local form description: ${formDescription}`);
        setLocalFormDescription(formDescription); // Sync local description with FormContext
      } else {
        console.log('[FormSettingsPage] formDescription is null or undefined, not updating localFormDescription.');
      }
    }
  }, [formTitle, formDescription, isFormLoaded]);

  // Handle title change locally
  const handleTitleChange = useCallback((event) => {
    // console.log(`[FormSettingsPage] Title changed locally to: ${event.target.value}`);
    setLocalFormTitle(event.target.value); // Update local state
    setIsModified(true); // Mark as modified
  }, []);

  // Handle description change locally
  const handleDescriptionChange = useCallback((event) => {
    // console.log(`[FormSettingsPage] Description changed locally to: ${event.target.value}`);
    setLocalFormDescription(event.target.value); // Update local state
    setIsModified(true); // Mark as modified
  }, []);

  // Save changes to FormContext
  const handleSave = useCallback(async () => {
    // Ensure no premature saving of stale or unmodified data
    if (!localFormTitle || !isModified) {
      // console.log('[FormSettingsPage] Save skipped - no title or no modifications detected.');
      return; // Ensure the title is filled and changes were made
    }

    setSaving(true);
    try {
      console.log(`[FormSettingsPage] Saving form details: Title: ${localFormTitle}, Description: ${localFormDescription}`);
      setFormTitle(localFormTitle); // Update the title in FormContext
      setFormDescription(localFormDescription); // Update the description in FormContext
      console.log('[FormSettingsPage] Form details saved successfully to FormContext.');
    } catch (error) {
      console.error(`[FormSettingsPage] Error saving form details: ${error.message}`);
    } finally {
      setSaving(false); // Stop the save loading spinner
      setIsModified(false); // Reset modified state
    }
  }, [localFormTitle, localFormDescription, isModified, setFormTitle, setFormDescription]);

  /**
   * Trigger a save on blur. If there's no change or the title is empty,
   * the save won't actually commit.
   */
  const handleBlur = useCallback(() => {
    // console.log('[FormSettingsPage] handleBlur triggered - attempting to save changes...');
    handleSave();
  }, [handleSave]);

  const handleVanitySaved = useCallback((slug, result) => {
    const normalized = slug || '';

    setInitialVanitySlug(normalized);

    console.log('[FormSettingsPage] Vanity URL saved:', {
      formId,
      vanitySlug: normalized || null,
      result,
    });

    // Rehydrate FormContext from Firestore so any other settings/page
    // consumers see the newly committed vanitySlug and vanityUrl.
    if (formId) {
      fetchFormDetails(formId).catch((error) => {
        console.warn(
          '[FormSettingsPage] Could not refresh form details after ' +
            'vanity save:',
          error
        );
      });
    }
  }, [fetchFormDetails, formId]);

  if (!isFormLoaded) {
    console.log('[FormSettingsPage] Form is still loading...');
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress size={48} /> {/* Loading Spinner while form is loading */}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: 3, // Adjust padding as needed to control the distance from the toolbar
      }}
    >
      {/* Form Settings Card */}
      <Paper
        elevation={3}
        sx={{
          padding: 4,
          maxWidth: 600, // Adjust the card width as needed
          width: '100%',
          textAlign: 'left', // Align text to the left
          mb: 3, // Add margin-bottom to separate from the next card
        }}
      >
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
          Form Settings
        </Typography>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0 }}>
          Edit form details and settings
        </Typography>

        {/* Non-editable Form ID displayed in smaller gray text above the Form Title */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 2 }}
        >
          Form ID: {formId}
        </Typography>

        <TextField
          label="Form Title"
          variant="outlined"
          fullWidth
          required // Make Form Title required
          value={localFormTitle}
          onChange={handleTitleChange} // Use local state
          onBlur={handleBlur} // Trigger save on blur
          sx={{ mb: 2 }}
          error={!localFormTitle} // Show error if the form title is empty
          helperText={!localFormTitle ? 'Form Title is required' : ''}
        />

        <TextField
          label="Form Description"
          variant="outlined"
          fullWidth
          multiline
          minRows={3}
          value={localFormDescription}
          onChange={handleDescriptionChange} // Use local state
          onBlur={handleBlur} // Trigger save on blur
          sx={{ mb: 1 }}
          helperText="Form Description (Optional)"
        />

        {/* ── Vanity URL section — matches the same width/structure as above */}
        <Stack spacing={1} sx={{ mb: 0 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Vanity URL
          </Typography>
          <VanityUrlField
            formId={formId}
            isPublished={!!isPublished}
            initialSlug={initialVanitySlug || ''}
            onSaved={handleVanitySaved}
          />
        </Stack>
      </Paper>


    </Box>
  );
};

export default FormSettingsPage;
